import { useRef, useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import "./canvas-draw-on-image.css";

const CANVAS_WIDTH = 900;
const CANVAS_HEIGHT = 450;
const MAX_HISTORY = 20;

const COLORS = [
  "#000000", "#6b7280", "#9ca3af", "#e5e7eb", "#ffffff",
  "#fca5a5", "#fde68a", "#86efac", "#93c5fd", "#fed7aa",
  "#ef4444", "#facc15", "#22c55e", "#3b82f6", "#f59e0b",
  "#b91c1c", "#ea580c", "#15803d", "#1d4ed8", "#8b5a3c",
];

const BRUSH_SIZES = [2, 4, 6, 8];

type Props = {
  imageUrl: string;
  initialData?: string;
  onSave?: (dataUrl: string) => void;
};

export default function CanvasDrawOnImage({
  imageUrl,
  initialData,
  onSave,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);

  const isDrawingRef = useRef(false);
  const [tool, setTool] = useState<"pen" | "eraser">("pen");
  const [color, setColor] = useState("#000000");
  const [brushSize, setBrushSize] = useState(4);
  const [showPalette, setShowPalette] = useState(false);
  const [zoomSrc, setZoomSrc] = useState<string | null>(null);

  const openZoom = () => {
    if (canvasRef.current) setZoomSrc(canvasRef.current.toDataURL("image/png"));
  };

  useEffect(() => {
    if (!zoomSrc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setZoomSrc(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoomSrc]);

  /* ---------- History ---------- */
  const history = useRef<string[]>([]);
  const historyIndex = useRef(-1);

  /* ---------- Debounce ---------- */
  const saveTimeout = useRef<number>();
  // Last data we emitted via onSave, and the image the canvas was last initialised with.
  // Used to skip re-initialising (clear + redraw = blink) when the parent just echoes our own save back.
  const lastSavedRef = useRef<string | null>(null);
  const initedImageUrlRef = useRef<string | null>(null);

  const debouncedSave = useCallback(() => {
    if (!onSave || !canvasRef.current) return;

    window.clearTimeout(saveTimeout.current);
    saveTimeout.current = window.setTimeout(() => {
      const dataUrl = canvasRef.current!.toDataURL("image/png");
      lastSavedRef.current = dataUrl;
      onSave(dataUrl);
    }, 400);
  }, [onSave]);

  /* ---------- Init Canvas ---------- */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Canvas already shows exactly this drawing; re-initialising would clear it and flash.
    if (
      initialData &&
      initialData === lastSavedRef.current &&
      initedImageUrlRef.current === imageUrl
    ) {
      return;
    }
    initedImageUrlRef.current = imageUrl;

    canvas.width = CANVAS_WIDTH;
    canvas.height = CANVAS_HEIGHT;

    const ctx = canvas.getContext("2d")!;
    ctx.lineCap = "round";
    ctxRef.current = ctx;

    const base = new Image();
    base.crossOrigin = "anonymous";
    base.src = imageUrl;

    base.onload = () => {
      ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      ctx.drawImage(base, 0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

      if (initialData) {
        const saved = new Image();
        saved.src = initialData;
        saved.onload = () => {
          ctx.drawImage(saved, 0, 0);
          applyBrush(); // 👈 re-apply color & size
        };
      } else {
        applyBrush(); // 👈 also here
      }

      pushHistory();
    };
  }, [imageUrl, initialData]);

  const applyBrush = () => {
    const ctx = ctxRef.current;
    if (!ctx) return;

    ctx.lineCap = "round";
    ctx.strokeStyle = tool === "eraser" ? "#ffffff" : color;
    ctx.lineWidth = brushSize;
  };
  /* ---------- Brush ---------- */
  useEffect(() => {
    const ctx = ctxRef.current;
    if (!ctx) return;

    ctx.strokeStyle = tool === "eraser" ? "#ffffff" : color;
    ctx.lineWidth = brushSize;
  }, [tool, color, brushSize]);

  /* ---------- History helpers ---------- */
  const pushHistory = () => {
    if (!canvasRef.current) return;

    const data = canvasRef.current.toDataURL();

    // Remove redo history
    history.current = history.current.slice(0, historyIndex.current + 1);

    history.current.push(data);

    // Enforce max history
    if (history.current.length > MAX_HISTORY) {
      history.current.shift();
    } else {
      historyIndex.current++;
    }

    // 🔑 ALWAYS clamp index to last item
    historyIndex.current = history.current.length - 1;
  };

  const restoreFromHistory = (index: number) => {
    if (!history.current[index]) return;

    const img = new Image();
    img.src = history.current[index];

    img.onload = () => {
      const ctx = ctxRef.current!;
      ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      ctx.drawImage(img, 0, 0);
      applyBrush();
    };
  };

  const undo = () => {
    if (historyIndex.current <= 0) return;
    historyIndex.current--;
    restoreFromHistory(historyIndex.current);
  };

  const redo = () => {
    if (historyIndex.current >= history.current.length - 1) return;
    historyIndex.current++;
    restoreFromHistory(historyIndex.current);
  };

  /* ---------- Drawing (Pointer Events = mobile ready) ---------- */
  const getPos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    const scaleX = CANVAS_WIDTH / rect.width;
    const scaleY = CANVAS_HEIGHT / rect.height;
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const ctx = ctxRef.current;
    if (!ctx) return;

    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    // draw a dot for a tap
    ctx.lineTo(x + 0.01, y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x, y);
    isDrawingRef.current = true;
  };

  const draw = (e: React.PointerEvent<HTMLCanvasElement>) => {
    // ref (not state) so moves right after pointerdown aren't dropped by stale renders
    if (!isDrawingRef.current) return;
    const ctx = ctxRef.current!;
    const native = e.nativeEvent;
    // coalesced events give every sampled touch point between frames
    const events =
      typeof native.getCoalescedEvents === "function" && native.getCoalescedEvents().length
        ? native.getCoalescedEvents()
        : [native];
    const rect = canvasRef.current!.getBoundingClientRect();
    const scaleX = CANVAS_WIDTH / rect.width;
    const scaleY = CANVAS_HEIGHT / rect.height;
    for (const ev of events) {
      ctx.lineTo((ev.clientX - rect.left) * scaleX, (ev.clientY - rect.top) * scaleY);
    }
    ctx.stroke();
    // restart path from the last point so stroke() doesn't re-render the whole path each move
    const last = events[events.length - 1];
    ctx.beginPath();
    ctx.moveTo((last.clientX - rect.left) * scaleX, (last.clientY - rect.top) * scaleY);
  };

  const stop = (e?: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;
    if (e?.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    ctxRef.current?.closePath();
    pushHistory();
    debouncedSave();
  };
  const getPencilCursor = (color: string) => {
    const svg = `

<svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
	 viewBox="0 0 401 401" style="enable-background:new 0 0 401 401;" xml:space="preserve">
<path fill="#fff" d="M380.8,33.5c13.7,13.6,13.7,35.8,0,49.4l-17.7,17.7L302,39.4l17.7-17.7c13.7-13.6,35.8-13.6,49.4,0L380.8,33.5z
	"/>
<path fill="#fff" d="M363.1,100.5L203.2,260.5h0L142,199.3L302,39.4L363.1,100.5z"/>
<path  d="M142,199.3l61.1,61.1l-0.6,0.6L118,284.5l23.4-84.6L142,199.3z"/>
<g id="Layer_2_1_">
	<g id="_01">
		<g id="_22.Period_Dot_or_Full_Stop">
			<path fill="${color}" d="M39.5,361.1c-7.3-6.5-11.4-15.9-11.2-25.7c-0.2-10,3.8-19.6,11.2-26.4c7.5-7.1,17.1-10.6,28.9-10.6
				c11.5,0,21,3.6,28.5,10.6c7.3,6.8,11.4,16.4,11.2,26.4c0.2,9.8-3.9,19.2-11.2,25.7c-7.5,7-17,10.5-28.5,10.5
				C56.6,371.6,47,368.1,39.5,361.1L39.5,361.1z"/>
		</g>
	</g>
</g>
<g>   
	<path  d="M376.1,14.7c-17.6-17.5-46-17.5-63.6,0L134.4,192.9c-1.2,1.2-2.1,2.7-2.6,4.4l-23.4,84.6c-1,3.5,0,7.2,2.6,9.7
		c2.5,2.5,6.3,3.5,9.7,2.6l84.6-23.4c1.7-0.5,3.2-1.3,4.4-2.6l0.6-0.6L387.8,89.9c17.6-17.5,17.6-46,0-63.5L376.1,14.7z
		 M146.8,218.2l37.6,37.6l-52,14.4L146.8,218.2z M203.2,260.5L203.2,260.5l6.5,6.5L203.2,260.5z M203.2,246.4l-47-47L302,53.5l47,47
		L203.2,246.4z M373.7,75.8l-10.6,10.6l-47-47l10.6-10.6c9.8-9.7,25.5-9.7,35.3,0l11.7,11.7C383.5,50.3,383.5,66.1,373.7,75.8z"/>
</g>
</svg>
  `;

    return `url("data:image/svg+xml;base64,${btoa(svg)}") 0 24, auto`;
  };
  /* ---------- Cursor ---------- */
  const cursor =
    tool === "pen"
      ? getPencilCursor(color)
      : tool === "eraser"
        ? 'url("/src/assets/cursors/eraser.png") 8 24, auto'
        : "default";
  return (
    <div className="annotator-root">
      <canvas
        ref={canvasRef}
        className="annotator-canvas"
        style={{ cursor }}
        onPointerDown={start}
        onPointerMove={draw}
        onPointerUp={stop}
        onPointerCancel={stop}
      />

      {/* <button type="button" className="zoom-btn" title="Full screen" onClick={openZoom}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M8 3H5a2 2 0 0 0-2 2v3" />
          <path d="M21 8V5a2 2 0 0 0-2-2h-3" />
          <path d="M3 16v3a2 2 0 0 0 2 2h3" />
          <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
        </svg>
      </button> */}

      {zoomSrc &&
        createPortal(
          <div className="zoom-overlay" onClick={() => setZoomSrc(null)}>
            <button type="button" className="zoom-close" title="Close" onClick={() => setZoomSrc(null)}>
              ✕
            </button>
            <img src={zoomSrc} className="zoom-image" alt="Zoomed" onClick={(e) => e.stopPropagation()} />
          </div>,
          document.body
        )}

      {/* Toolbar */}
      <div className="toolbar">
        <button className={`tool-btn ${tool === "pen" ? "active" : ""}`} onClick={() => setTool("pen")}>
          <svg id="fi_2985043" fill="#fff" enable-background="new 0 0 64 64" height="20" viewBox="0 0 64 64" width="20" xmlns="http://www.w3.org/2000/svg"><g><path d="m11.105 43.597c-.512 0-1.023-.195-1.414-.586-.781-.781-.781-2.047 0-2.828l31.254-31.254c.78-.781 2.047-.781 2.828 0s.781 2.047 0 2.828l-31.254 31.254c-.39.39-.902.586-1.414.586z"></path></g><g><path d="m8.017 58c-.139 0-.28-.015-.421-.045-1.08-.232-1.768-1.295-1.536-2.375l3.09-14.403c.232-1.08 1.3-1.766 2.375-1.536 1.08.232 1.768 1.295 1.536 2.375l-3.09 14.403c-.201.939-1.031 1.581-1.954 1.581z"></path></g><g><path d="m22.418 54.91c-.512 0-1.023-.195-1.414-.586-.781-.781-.781-2.047 0-2.828l31.254-31.253c.78-.781 2.047-.781 2.828 0s.781 2.047 0 2.828l-31.253 31.253c-.391.391-.903.586-1.415.586z"></path></g><g><path d="m8.013 58c-.923 0-1.752-.642-1.954-1.581-.231-1.08.456-2.143 1.536-2.375l14.403-3.09c1.081-.229 2.144.457 2.375 1.536.231 1.08-.456 2.143-1.536 2.375l-14.403 3.09c-.141.031-.282.045-.421.045z"></path></g><g><path d="m48.015 29.313c-.512 0-1.024-.195-1.414-.586l-11.313-11.313c-.781-.781-.781-2.047 0-2.828.78-.781 2.048-.781 2.828 0l11.313 11.313c.781.781.781 2.047 0 2.828-.39.391-.902.586-1.414.586z"></path></g><g><path d="m53.672 23.657c-.512 0-1.024-.195-1.415-.586-.781-.781-.781-2.047 0-2.829 1.113-1.113 1.726-2.62 1.726-4.242s-.613-3.129-1.726-4.242c-1.114-1.114-2.621-1.727-4.243-1.727s-3.129.613-4.242 1.727c-.78.781-2.046.782-2.829 0-.781-.781-.781-2.047 0-2.829 1.868-1.869 4.379-2.898 7.071-2.898 2.691 0 5.203 1.029 7.071 2.898 1.869 1.868 2.898 4.379 2.898 7.071s-1.029 5.203-2.898 7.071c-.389.39-.901.586-1.413.586z"></path></g></svg>
        </button>

  <button className="tool-btn" onClick={undo}>
          <svg id="fi_18383689" fill="#fff" width="18" height="18" enable-background="new 0 0 100 100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"><path d="m97.5 59.5550575c-.0165863 14.2973061-11.6039658 25.8827705-25.9012833 25.8969841h-14.3432808c-2.3738594 0-4.2982559-1.9244003-4.2982559-4.2982559s1.9243965-4.2982559 4.2982559-4.2982559h14.3432732c9.5559769.1506348 17.424736-7.4739075 17.5753708-17.0298729.1506348-9.5559807-7.4738998-17.4247398-17.0298691-17.5753746-.1818237-.0028648-.363678-.0028648-.5455017 0h-53.0189743l13.7071381 11.5365105c1.8254776 1.5175095 2.0751343 4.2275467.557621 6.0530205-1.5175133 1.8254776-4.2275429 2.0751305-6.0530186.5576248-.0136585-.0113564-.0272427-.0227928-.0407581-.0343132l-22.7205739-19.1229363c-1.8160033-1.5288391-2.0487952-4.2403679-.5199568-6.0563698.1581488-.1878548.3321037-.3618088.5199568-.5199585l22.7205734-19.1229362c1.8255215-1.5174589 4.5355453-1.2677231 6.0530052.5577993 1.5041122 1.8094692 1.2737465 4.4920006-.5168495 6.0185299l-13.7028384 11.5408125h53.0146751c14.2963409.0165749 25.8823471 11.6006546 25.9012909 25.8969917z"></path></svg>
        </button>
        <button className="tool-btn" onClick={redo}>
          <svg xmlns="http://www.w3.org/2000/svg" version="1.1" fill="#fff" width="18" height="" x="0" y="0" viewBox="0 0 100 100" ><g transform="matrix(-1,0,0,1,100.00000476837158,0)"><path d="M97.5 59.555c-.017 14.297-11.604 25.883-25.901 25.897H57.255a4.298 4.298 0 0 1 0-8.596H71.6c9.556.15 17.424-7.474 17.575-17.03.15-9.556-7.474-17.425-17.03-17.576a17.313 17.313 0 0 0-.545 0h-53.02l13.708 11.537a4.298 4.298 0 1 1-5.536 6.576L4.03 41.24a4.298 4.298 0 0 1 0-6.576L26.75 15.54a4.298 4.298 0 0 1 5.536 6.576L18.584 33.658h53.015c14.296.017 25.882 11.6 25.901 25.897z" opacity="1" data-original="#000000"></path></g></svg>
        </button>

        <div className="divider" />

        {BRUSH_SIZES.map((s) => (
          <button key={s} className={`size-btn ${brushSize === s ? "active" : ""}`} onClick={() => setBrushSize(s)}>
            <span style={{ width: s * 2, height: s * 2 }} />
          </button>
        ))}

        <div className="divider" />

        <button className="color-btn" onClick={() => setShowPalette((v) => !v)}>
          <span style={{ background: color }} />
        </button>
      </div>

      {showPalette && (
        <div className="color-palette">
          {COLORS.map((c) => (
            <button
              key={c}
              className={`color-swatch ${color === c ? "active" : ""}`}
              style={{ background: c }}
              onClick={() => {
                setColor(c);
                setShowPalette(false);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
